// ==================== ANALYTICS TRACKING SYSTEM ====================
// Handles both Google Analytics 4 and custom Supabase analytics

import { supabase, isSupabaseConfigured } from './supabase';
import { logger } from './logger';

// ==================== SESSION & DEVICE UTILS ====================
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('ja_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('ja_session_id', sessionId);
  }
  return sessionId;
};

const getDeviceType = () => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// ==================== GA4 INTEGRATION ====================
let ga4Initialized = false;
let ga4MeasurementId = null;
let ga4InitPromise = null;
let pendingGA4Events = [];

export const initGA4 = (measurementId) => {
  if (!measurementId) return Promise.resolve(false);
  if (ga4Initialized) return Promise.resolve(true);
  if (ga4InitPromise) return ga4InitPromise;

  ga4InitPromise = new Promise((resolve) => {
    // Load gtag.js script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.async = true;

    script.onload = () => {
      // Initialize gtag after script loads
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false // We'll manually track page views
      });

      ga4MeasurementId = measurementId;
      ga4Initialized = true;
      logger.log('GA4 initialized with:', measurementId);

      // Flush any pending events that were queued before initialization
      if (pendingGA4Events.length > 0) {
        logger.log(`Flushing ${pendingGA4Events.length} pending GA4 events`);
        pendingGA4Events.forEach(({ eventName, params }) => {
          window.gtag('event', eventName, params);
        });
        pendingGA4Events = [];
      }

      resolve(true);
    };

    script.onerror = () => {
      logger.error('Failed to load GA4 script');
      ga4InitPromise = null;
      resolve(false);
    };

    document.head.appendChild(script);
  });

  return ga4InitPromise;
};

// Check if GA4 is ready
export const isGA4Ready = () => ga4Initialized;

const trackGA4Event = (eventName, params = {}) => {
  // If GA4 is initialized, send immediately
  if (ga4Initialized && window.gtag) {
    window.gtag('event', eventName, params);
    return;
  }

  // If GA4 is being initialized, queue the event
  if (ga4InitPromise) {
    pendingGA4Events.push({ eventName, params });
    logger.log('GA4 event queued:', eventName);
    return;
  }

  // GA4 not configured, silently skip
};

// ==================== CUSTOM ANALYTICS (SUPABASE) ====================
const trackCustomEvent = async (eventType, data = {}) => {
  if (!isSupabaseConfigured()) return;

  try {
    const { error } = await supabase.rpc('record_analytics_event', {
      p_event_type: eventType,
      p_page: data.page || null,
      p_entity_type: data.entityType || null,
      p_entity_id: data.entityId || null,
      p_entity_name: data.entityName || null,
      p_user_id: data.userId || null,
      p_session_id: getSessionId(),
      p_parish: data.parish || null,
      p_device_type: getDeviceType(),
      p_referrer: document.referrer || null,
      p_metadata: data.metadata || {}
    });

    if (error) logger.error('Analytics error:', error);
  } catch (err) {
    logger.error('Failed to track event:', err);
  }
};

// ==================== UNIFIED TRACKING FUNCTIONS ====================

// Track page views
export const trackPageView = (pageName, pageTitle) => {
  // GA4
  trackGA4Event('page_view', {
    page_title: pageTitle || pageName,
    page_location: window.location.href,
    page_path: `/#${pageName}`
  });

  // Custom
  trackCustomEvent('page_view', {
    page: pageName,
    metadata: { title: pageTitle }
  });
};

// Track offence views
export const trackOffenceView = (offenceCode, offenceTitle, category) => {
  trackGA4Event('view_item', {
    content_type: 'offence',
    item_id: offenceCode,
    item_name: offenceTitle,
    item_category: category
  });

  trackCustomEvent('offence_view', {
    page: 'offences',
    entityType: 'offence',
    entityId: offenceCode,
    entityName: offenceTitle,
    metadata: { category }
  });
};

// Track directory listing views
export const trackDirectoryView = (type, id, name, parish) => {
  trackGA4Event('view_item', {
    content_type: type,
    item_id: id,
    item_name: name,
    item_category: type
  });

  trackCustomEvent('directory_view', {
    page: type,
    entityType: type,
    entityId: id,
    entityName: name,
    parish
  });

  // Increment listing view count
  if (isSupabaseConfigured() && supabase) {
    try {
      const result = supabase.rpc('increment_listing_view', { p_type: type, p_id: id });
      if (result && typeof result.catch === 'function') {
        result.catch((err) => logger.warn('Failed to increment listing view:', err?.message));
      }
    } catch (err) {
      logger.warn('Failed to increment listing view:', err?.message);
    }
  }
};

// Track directory listing clicks (phone, email, website)
export const trackDirectoryClick = (type, id, name, clickType, parish) => {
  trackGA4Event('select_content', {
    content_type: type,
    item_id: id,
    item_name: name,
    click_type: clickType
  });

  trackCustomEvent('directory_click', {
    page: type,
    entityType: type,
    entityId: id,
    entityName: name,
    parish,
    metadata: { clickType }
  });

  // Increment listing click count
  if (isSupabaseConfigured() && supabase) {
    try {
      const result = supabase.rpc('increment_listing_click', { p_type: type, p_id: id });
      if (result && typeof result.catch === 'function') {
        result.catch((err) => logger.warn('Failed to increment listing click:', err?.message));
      }
    } catch (err) {
      logger.warn('Failed to increment listing click:', err?.message);
    }
  }
};

// Track ad impressions
export const trackAdImpression = (adId, adName, placement, page) => {
  trackGA4Event('ad_impression', {
    ad_id: adId,
    ad_name: adName,
    ad_placement: placement,
    page_location: page
  });

  trackCustomEvent('ad_impression', {
    page,
    entityType: 'ad',
    entityId: adId,
    entityName: adName,
    metadata: { placement }
  });

  // Increment ad impression count
  if (isSupabaseConfigured() && supabase) {
    try {
      const result = supabase.rpc('increment_ad_impression', { ad_id: adId });
      if (result && typeof result.catch === 'function') {
        result.catch((err) => logger.warn('Failed to increment ad impression:', err?.message));
      }
    } catch (err) {
      logger.warn('Failed to increment ad impression:', err?.message);
    }
  }
};

// Track ad clicks
export const trackAdClick = (adId, adName, placement, page, targetUrl) => {
  trackGA4Event('ad_click', {
    ad_id: adId,
    ad_name: adName,
    ad_placement: placement,
    page_location: page,
    target_url: targetUrl
  });

  trackCustomEvent('ad_click', {
    page,
    entityType: 'ad',
    entityId: adId,
    entityName: adName,
    metadata: { placement, targetUrl }
  });

  // Increment ad click count
  if (isSupabaseConfigured() && supabase) {
    try {
      const result = supabase.rpc('increment_ad_click', { ad_id: adId });
      if (result && typeof result.catch === 'function') {
        result.catch((err) => logger.warn('Failed to increment ad click:', err?.message));
      }
    } catch (err) {
      logger.warn('Failed to increment ad click:', err?.message);
    }
  }
};

// Track searches
export const trackSearch = (query, resultCount, page = 'offences') => {
  trackGA4Event('search', {
    search_term: query,
    search_results: resultCount
  });

  trackCustomEvent('search', {
    page,
    metadata: { query, resultCount }
  });
};

// ==================== ANALYTICS DATA FETCHING ====================
export const analyticsAPI = {
  // Get summary for date range
  getSummary: async (startDate, endDate, entityType = null, parish = null) => {
    if (!isSupabaseConfigured()) return { data: null, error: 'Not configured' };

    const { data, error } = await supabase.rpc('get_analytics_summary', {
      p_start_date: startDate,
      p_end_date: endDate,
      p_entity_type: entityType,
      p_parish: parish
    });

    return { data: data?.[0], error };
  },

  // Get top entities (offences, lawyers, etc.)
  getTopEntities: async (startDate, endDate, entityType, limit = 10) => {
    if (!isSupabaseConfigured()) return { data: [], error: 'Not configured' };

    return await supabase.rpc('get_top_entities', {
      p_start_date: startDate,
      p_end_date: endDate,
      p_entity_type: entityType,
      p_limit: limit
    });
  },

  // Get breakdown by parish
  getByParish: async (startDate, endDate) => {
    if (!isSupabaseConfigured()) return { data: [], error: 'Not configured' };

    return await supabase.rpc('get_analytics_by_parish', {
      p_start_date: startDate,
      p_end_date: endDate
    });
  },

  // Get raw events with filters
  getEvents: async (filters = {}) => {
    if (!isSupabaseConfigured()) return { data: [], error: 'Not configured' };

    let query = supabase
      .from('analytics_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(filters.limit || 100);

    if (filters.eventType) query = query.eq('event_type', filters.eventType);
    if (filters.entityType) query = query.eq('entity_type', filters.entityType);
    if (filters.entityId) query = query.eq('entity_id', filters.entityId);
    if (filters.parish) query = query.eq('parish', filters.parish);
    if (filters.startDate) query = query.gte('created_at', filters.startDate);
    if (filters.endDate) query = query.lte('created_at', filters.endDate);

    return await query;
  },

  // Get advertiser's own listing stats
  getMyListingStats: async (userId) => {
    if (!isSupabaseConfigured()) return { data: null, error: 'Not configured' };

    // Get all listings owned by user from unified service_providers table
    const { data, error } = await supabase
      .from('service_providers')
      .select('id, name, type, view_count, click_count, tier')
      .eq('owner_id', userId);

    if (error) return { data: [], error };

    // Map to expected format (view_count -> total_views for backwards compatibility)
    const listings = (data || []).map(p => ({
      ...p,
      total_views: p.view_count || 0,
      total_clicks: p.click_count || 0
    }));

    return { data: listings, error: null };
  }
};

export default {
  initGA4,
  isGA4Ready,
  trackPageView,
  trackOffenceView,
  trackDirectoryView,
  trackDirectoryClick,
  trackAdImpression,
  trackAdClick,
  trackSearch,
  analyticsAPI
};
